import { Link, useParams } from 'react-router-dom'
import { api } from '../../api/public'
import { Empty, Failed, Loading } from '../../components/Async'
import { useApi } from '../../components/useApi'
import { useSeason } from './useSeason'
import { SeasonSwitcher } from './SeasonSwitcher'
import { usePlayerPaths } from '../../components/playerPaths'

/**
 * Season stat leaders (FR-039i).
 *
 * # Where it was set is half the statistic
 *
 * "Steve, 38" is a trivia item. "Steve, 38 — week 9 at Lodestone" is something
 * two people can argue about, and it is checkable: the round is a link. The
 * requirement names the round and the course for exactly that reason, so they
 * are rendered on the row rather than kept for a detail view.
 *
 * # A low round competes only against its own length
 *
 * The league plays fifteen nine-hole weeks and two eighteens, and a nine is
 * always the lower number — so one pooled "low round of the season" would mean
 * the shortest round of the season, and it would look like an achievement. The
 * server returns one per hole count and this shows both.
 *
 * # Nobody is a zero, not a gap
 *
 * A season in which no birdie has been made yet still has a birdies row, at
 * nought. Dropping it would make an early season look as though the league had
 * stopped counting — which is the opposite failure from FR-039s's misleading
 * zero, and just as wrong.
 */
export function Leaders() {
  const { pathFor } = usePlayerPaths()
  const { seasonId } = useParams()
  const season = useSeason(seasonId)
  const id = season.state === 'ready' ? season.data.id : 0
  const leaders = useApi((s) => (id ? api.leaders(id, s) : new Promise<never>(() => {})), [id])

  if (season.state === 'loading') return <Loading what="the season" />
  if (season.state === 'error') return <Failed what="The season" error={season.error} />
  if (leaders.state === 'loading') return <Loading what="the leaders" />
  if (leaders.state === 'error') return <Failed what="The leaders" error={leaders.error} />

  const rows = leaders.data
  return (
    <section className="leaders" data-testid="leaders">
      <h2>{season.data.name} leaders</h2>
      <SeasonSwitcher current={id} view="leaders" />

      {rows.length === 0 ? (
        <Empty>No event of this season has been completed yet, so nothing has been set.</Empty>
      ) : (
        <ul className="leader-list" data-testid="leader-list">
          {rows.map((l, i) => (
            <li key={`${l.metric}-${i}`} className="leader" data-testid={`leader-${l.metric}`}>
              <span className="leader-metric">{leaderLabel(l.metric)}</span>
              {l.nobody ? (
                <span className="leader-nobody">
                  <strong>Nobody yet</strong> — no {leaderNoun(l.metric)} has been made this season.
                </span>
              ) : (
                <>
                  <span className="leader-who">
                    <Link to={pathFor(l.player_id)}>{l.display_name}</Link>
                    {l.tied_with.length > 0 && (
                      /* Two players on the same figure are two leaders. Picking
                         one would be wrong about the season (FR-032's
                         reasoning, applied to a leaderboard). */
                      <em className="mark"> tied with {l.tied_with.length} other</em>
                    )}
                  </span>
                  {/* A decimal string, printed as it arrived. */}
                  <span className="leader-value">{l.value}</span>
                  <span className="leader-where">
                    {l.event_id ? (
                      <Link to={`/events/${l.event_id}`}>
                        {l.week_number ? `Week ${l.week_number}` : `Event ${l.event_id}`}
                        {l.course && ` · ${l.course}`}
                      </Link>
                    ) : (
                      <span className="note">over the season</span>
                    )}
                  </span>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      <p className="note">
        <Link to={`/stats?season_id=${id}`}>All statistics for this season</Link> · Playoff rounds
        award no points, and a low round is compared only against rounds of the same length.
      </p>
    </section>
  )
}

function leaderLabel(metric: string): string {
  const labels: Record<string, string> = {
    low_gross_round: 'Low gross round',
    low_net_round: 'Low net round',
    birdies: 'Birdies',
    pars: 'Pars',
    bogeys: 'Bogeys',
  }
  return labels[metric] ?? metric.replace(/_/g, ' ')
}

function leaderNoun(metric: string): string {
  switch (metric) {
    case 'birdies':
      return 'birdie'
    case 'pars':
      return 'par'
    case 'bogeys':
      return 'bogey'
    default:
      return 'score'
  }
}
